/**
 * BrandSpaceIssueNotice
 *
 * Inline, friendly explanation for Brand Space limits when creating or reactivating a brand,
 * with an upsell action to the Shop. Replaces raw error toasts.
 *
 *  - full          -> "All your Brand Spaces are in use" + Add Brand Spaces / Upgrade your Plan
 *  - hiddenByPlan  -> "Paused by Plan change" + Upgrade your Plan
 *  - cooldown      -> "You can reactivate this brand on {date}" (no upsell: time-based, not capacity)
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { BrandSpaceAction, BrandSpaceIssue } from '@/utils/brandSpaceErrors';

interface BrandSpaceIssueNoticeProps {
  issue: BrandSpaceIssue;
  action: BrandSpaceAction;
  /** Called before navigating to the Shop (e.g. to close the surrounding modal) */
  onNavigate?: () => void;
  className?: string;
}

export const BrandSpaceIssueNotice = ({ issue, action, onNavigate, className = '' }: BrandSpaceIssueNoticeProps) => {
  const { t } = useTranslation('agency');
  const { formatDateOnly } = useTimezone();
  const navigate = useNavigate();

  const goToShop = () => {
    onNavigate?.();
    navigate('/shop');
  };

  let title: string;
  let body: string;
  let detail: string | null = null;
  let ctaLabel: string | null = null;

  if (issue.kind === 'full') {
    title = t('brand_space_notice_full_title');
    body =
      action === 'create'
        ? t('brand_space_notice_full_body_create')
        : t('brand_space_notice_full_body_reactivate');
    if (issue.used !== null && issue.total !== null) {
      detail = t('brand_space_notice_full_detail', { used: issue.used, total: issue.total });
    }
    ctaLabel = t('brand_space_cta_shop');
  } else if (issue.kind === 'hiddenByPlan') {
    title = t('brand_space_notice_hidden_title');
    body = t('brand_space_notice_hidden_body');
    ctaLabel = t('brand_space_cta_upgrade');
  } else {
    title = t('brand_space_notice_cooldown_title');
    body = issue.availableAt
      ? t('brand_space_notice_cooldown_body', { date: formatDateOnly(issue.availableAt) })
      : t('brand_space_notice_cooldown_body_nodate');
  }

  return (
    <div role="alert" className={`rounded-xl border border-peach bg-peach/20 px-4 py-3 ${className}`}>
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-700 mt-0.5">
        {body}
        {detail && <span className="text-gray-500"> {detail}</span>}
      </p>
      {ctaLabel && (
        <Button type="button" variant="primary" size="sm" className="mt-3" onClick={goToShop}>
          {ctaLabel}
        </Button>
      )}
    </div>
  );
};

export default BrandSpaceIssueNotice;
